import { useEffect, useMemo, useState } from "react";
import axios from "../../../api/axios";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DataTable from "examples/Tables/DataTable";

const today = () => new Date().toISOString().slice(0, 10);

export default function TaxPolicies() {
  const [policies, setPolicies] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(false);

  const [activeMonth, setActiveMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [activePolicy, setActivePolicy] = useState(null);

  const [form, setForm] = useState({
    name: "Default PIT Policy",
    effective_from: today(),
    effective_to: "",
    rent_relief_mode: "temporary_flat",
    rent_flat_amount: 0,
    actual_rent_cap: 0,
    tiered_caps: [
      // default template
      { max_gross: 300000, cap: 60000 },
      { max_gross: 600000, cap: 100000 },
      { max_gross: null, cap: 150000 },
    ],
    is_active: true,
  });

  const loadPolicies = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/tax/policies", { params: { per_page: 200 } });
      setPolicies(res.data?.data || []);
    } finally {
      setLoading(false);
    }
  };

  const loadAudit = async () => {
    const res = await axios.get("/tax/policies/audit", { params: { per_page: 50 } });
    setAudit(res.data?.data || []);
  };

  const loadActive = async () => {
    const res = await axios.get("/tax/policies/active", { params: { month: activeMonth } });
    setActivePolicy(res.data?.policy || null);
  };

  useEffect(() => {
    loadPolicies();
    loadAudit();
    loadActive();
  }, []);

  const createPolicy = async () => {
    const payload = {
      ...form,
      effective_to: form.effective_to || null,
      tiered_caps: form.rent_relief_mode === "tiered_cap" ? form.tiered_caps : null,
      rent_flat_amount:
        form.rent_relief_mode === "temporary_flat" ? Number(form.rent_flat_amount || 0) : null,
      actual_rent_cap:
        form.rent_relief_mode === "actual_rent" ? Number(form.actual_rent_cap || 0) : null,
    };

    await axios.post("/tax/policies", payload);
    await loadPolicies();
    await loadAudit();
    await loadActive();
    alert("Policy created");
  };

  const updatePolicy = async (id, patch) => {
    await axios.put(`/tax/policies/${id}`, patch);
    await loadPolicies();
    await loadAudit();
    await loadActive();
  };

  const addTierRow = () => {
    setForm({ ...form, tiered_caps: [...form.tiered_caps, { max_gross: null, cap: 0 }] });
  };

  const updateTier = (idx, key, value) => {
    const next = [...form.tiered_caps];
    next[idx] = { ...next[idx], [key]: value === "" ? null : value };
    setForm({ ...form, tiered_caps: next });
  };

  const removeTier = (idx) => {
    const next = form.tiered_caps.filter((_, i) => i !== idx);
    setForm({ ...form, tiered_caps: next });
  };

  const policiesColumns = useMemo(
    () => [
      { Header: "ID", accessor: "id" },
      { Header: "Name", accessor: "name" },
      { Header: "From", accessor: "effective_from" },
      { Header: "To", accessor: "effective_to" },
      { Header: "Mode", accessor: "rent_relief_mode" },
      {
        Header: "Active",
        accessor: "is_active",
        disableSortBy: true,
        Cell: (cell) => {
          const r = cell?.row?.original || {};
          const active = !!r?.is_active;

          return (
            <MDButton
              size="small"
              color={active ? "success" : "dark"}
              disabled={!r?.id || loading}
              onClick={() => updatePolicy(r.id, { is_active: !active })}
            >
              {active ? "Active" : "Inactive"}
            </MDButton>
          );
        },
      },
      {
        Header: "Edit",
        accessor: "edit",
        disableSortBy: true,
        Cell: (cell) => {
          const r = cell?.row?.original || {};

          return (
            <MDBox display="flex" gap={1} flexWrap="wrap">
              <MDButton
                size="small"
                color="info"
                disabled={!r?.id || loading}
                onClick={() => {
                  const name = window.prompt("New name:", r.name || "");
                  if (name && name.trim()) updatePolicy(r.id, { name: name.trim() });
                }}
              >
                Rename
              </MDButton>

              <MDButton
                size="small"
                color="secondary"
                disabled={!r?.id || loading}
                onClick={() => {
                  const mode = window.prompt(
                    "Set rent_relief_mode: temporary_flat | actual_rent | tiered_cap",
                    r.rent_relief_mode || "temporary_flat"
                  );
                  if (mode && mode.trim()) updatePolicy(r.id, { rent_relief_mode: mode.trim() });
                }}
              >
                Mode
              </MDButton>
            </MDBox>
          );
        },
      },
    ],
    // ✅ include what you use inside memo
    [loading, updatePolicy]
  );

  const auditColumns = useMemo(
    () => [
      { Header: "ID", accessor: "id" },
      { Header: "Policy", accessor: "tax_policy_id" },
      { Header: "Action", accessor: "action" },
      { Header: "User", accessor: "user_id" },
      { Header: "At", accessor: "created_at" },
    ],
    []
  );

  return (
    <MDBox p={3}>
      <MDTypography variant="h5">Tax Policies (Rent Relief Modes + Audit)</MDTypography>

      {/* Active policy for month */}
      <MDBox mt={2} p={2} sx={{ border: "1px solid #eee", borderRadius: 2 }}>
        <MDTypography variant="h6">Active Policy For Month</MDTypography>
        <MDBox mt={1} display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <MDBox width={160}>
            <MDInput
              label="Month (YYYY-MM)"
              value={activeMonth}
              onChange={(e) => setActiveMonth(e.target.value)}
            />
          </MDBox>
          <MDButton color="info" onClick={loadActive}>
            Check
          </MDButton>
          {activePolicy ? (
            <MDTypography variant="button" color="text">
              Policy #{activePolicy.id} • {activePolicy.rent_relief_mode} • Effective From{" "}
              {activePolicy.effective_from}
            </MDTypography>
          ) : (
            <MDTypography variant="button" color="error">
              No active policy for this month
            </MDTypography>
          )}
        </MDBox>
      </MDBox>

      {/* Create policy */}
      <MDBox mt={3} p={2} sx={{ border: "1px solid #eee", borderRadius: 2 }}>
        <MDTypography variant="h6">Create New Policy</MDTypography>

        <MDBox mt={1} display="flex" gap={2} flexWrap="wrap">
          <MDBox width={260}>
            <MDInput
              label="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </MDBox>
          <MDBox width={160}>
            <MDInput
              label="Effective From"
              value={form.effective_from}
              onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
            />
          </MDBox>
          <MDBox width={160}>
            <MDInput
              label="Effective To (optional)"
              value={form.effective_to}
              onChange={(e) => setForm({ ...form, effective_to: e.target.value })}
            />
          </MDBox>
        </MDBox>

        <MDBox mt={2} display="flex" gap={1} flexWrap="wrap">
          <MDButton
            size="small"
            color={form.rent_relief_mode === "temporary_flat" ? "info" : "dark"}
            onClick={() => setForm({ ...form, rent_relief_mode: "temporary_flat" })}
          >
            temporary_flat
          </MDButton>
          <MDButton
            size="small"
            color={form.rent_relief_mode === "actual_rent" ? "info" : "dark"}
            onClick={() => setForm({ ...form, rent_relief_mode: "actual_rent" })}
          >
            actual_rent
          </MDButton>
          <MDButton
            size="small"
            color={form.rent_relief_mode === "tiered_cap" ? "info" : "dark"}
            onClick={() => setForm({ ...form, rent_relief_mode: "tiered_cap" })}
          >
            tiered_cap
          </MDButton>

          <MDButton
            size="small"
            color={form.is_active ? "success" : "dark"}
            onClick={() => setForm({ ...form, is_active: !form.is_active })}
          >
            {form.is_active ? "Active" : "Inactive"}
          </MDButton>
        </MDBox>

        {form.rent_relief_mode === "temporary_flat" && (
          <MDBox mt={2} width={220}>
            <MDInput
              label="Rent Flat Amount"
              type="number"
              value={form.rent_flat_amount}
              onChange={(e) => setForm({ ...form, rent_flat_amount: e.target.value })}
            />
          </MDBox>
        )}

        {form.rent_relief_mode === "actual_rent" && (
          <MDBox mt={2} width={220}>
            <MDInput
              label="Actual Rent Cap"
              type="number"
              value={form.actual_rent_cap}
              onChange={(e) => setForm({ ...form, actual_rent_cap: e.target.value })}
            />
          </MDBox>
        )}

        {form.rent_relief_mode === "tiered_cap" && (
          <MDBox mt={2}>
            <MDTypography variant="button" color="text">
              Tiered Caps (annual_gross threshold → cap). Set max_gross to blank for last tier.
            </MDTypography>

            <MDBox mt={1} display="flex" gap={1} flexDirection="column">
              {form.tiered_caps.map((t, idx) => (
                <MDBox key={idx} display="flex" gap={2} flexWrap="wrap" alignItems="center">
                  <MDBox width={220}>
                    <MDInput
                      label="max_gross (nullable)"
                      type="number"
                      value={t.max_gross ?? ""}
                      onChange={(e) =>
                        updateTier(
                          idx,
                          "max_gross",
                          e.target.value === "" ? null : Number(e.target.value)
                        )
                      }
                    />
                  </MDBox>
                  <MDBox width={220}>
                    <MDInput
                      label="cap"
                      type="number"
                      value={t.cap}
                      onChange={(e) => updateTier(idx, "cap", Number(e.target.value || 0))}
                    />
                  </MDBox>
                  <MDButton size="small" color="error" onClick={() => removeTier(idx)}>
                    Remove
                  </MDButton>
                </MDBox>
              ))}
            </MDBox>

            <MDBox mt={2} display="flex" gap={1}>
              <MDButton size="small" color="secondary" onClick={addTierRow}>
                Add Tier
              </MDButton>
            </MDBox>
          </MDBox>
        )}

        <MDBox mt={2} display="flex" gap={2}>
          <MDButton color="info" onClick={createPolicy} disabled={loading}>
            Create Policy
          </MDButton>
          <MDButton
            color="secondary"
            onClick={async () => {
              await loadPolicies();
              await loadAudit();
            }}
          >
            Refresh Lists
          </MDButton>
        </MDBox>
      </MDBox>

      {/* Policies list */}
      <MDBox mt={3}>
        <MDTypography variant="h6">Policies</MDTypography>
        <DataTable
          table={{ columns: policiesColumns, rows: policies }}
          entriesPerPage={false}
          showTotalEntries={false}
          canSearch
          pagination
          noEndBorder
        />
      </MDBox>

      {/* Audit */}
      <MDBox mt={3}>
        <MDTypography variant="h6">Policy Audit Logs</MDTypography>
        <DataTable
          table={{ columns: auditColumns, rows: audit }}
          entriesPerPage={false}
          showTotalEntries={false}
          canSearch={false}
          pagination
          noEndBorder
        />
      </MDBox>
    </MDBox>
  );
}
